import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { BusinessDayRangePicker } from "@/components/shared"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SHEET_WIDTH,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { fmtDate } from "@/lib/format"
import { api, apiError } from "@/services/api"
import type { ClosureRangeResult } from "@/types"

interface ClosureRangeSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Betriebsurlaubs-Zeitraum setzen/aufheben (G-33). Die Response liefert die
 * Vorschau `effectiveDays` — nur Tage, die dadurch BETRIEBSURLAUB werden/waren
 * (Feiertage + Wochenenden bleiben unberuehrt, Kaskade G-32).
 */
export function ClosureRangeSheet({ open, onOpenChange }: ClosureRangeSheetProps) {
  const queryClient = useQueryClient()
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [name, setName] = useState("")
  const [result, setResult] = useState<ClosureRangeResult | null>(null)

  const mutation = useMutation({
    mutationFn: (setClosure: boolean) =>
      api.calendar.closureRange({
        from: dateFrom,
        to: dateTo,
        name: name.trim() || null,
        set: setClosure,
      }),
    onSuccess: (data) => {
      setResult(data)
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      toast.success(
        data.set
          ? `Betriebsurlaub gesetzt (${data.effectiveDays.length} wirksame Tage)`
          : `Betriebsurlaub aufgehoben (${data.removedEntries} Eintrag/Einträge, ${data.effectiveDays.length} Tage)`
      )
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const valid = dateFrom !== "" && dateTo !== "" && dateFrom <= dateTo

  function handleOpenChange(next: boolean) {
    if (!next) {
      setResult(null)
      mutation.reset()
    }
    onOpenChange(next)
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent sheetKey="calendar-closure" defaultWidth={SHEET_WIDTH.xl} className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Betriebsurlaub</SheetTitle>
          <SheetDescription>
            Zeitraum als Betriebsurlaub setzen oder aufheben. Feiertage und Wochenenden im
            Zeitraum bleiben unberührt.
          </SheetDescription>
        </SheetHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="closure-from">Von</Label>
              <Input
                id="closure-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="closure-to">Bis</Label>
              <Input
                id="closure-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>

          {/* Picker-Zaehler = Vorab-Schaetzung; die Server-Vorschau (effectiveDays)
              nach dem Speichern bleibt massgeblich */}
          <BusinessDayRangePicker
            from={dateFrom}
            to={dateTo}
            onChange={(f, t) => {
              setDateFrom(f)
              setDateTo(t)
            }}
          />

          <div className="space-y-1.5">
            <Label htmlFor="closure-name">Name (optional)</Label>
            <Input
              id="closure-name"
              placeholder="z. B. Betriebsferien Sommer"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {result && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="mb-1 font-medium">
                {result.set ? "Gesetzt" : "Aufgehoben"} — {result.effectiveDays.length} wirksame{" "}
                {result.effectiveDays.length === 1 ? "Tag" : "Tage"}
              </p>
              {result.effectiveDays.length > 0 ? (
                <div className="max-h-28 overflow-y-auto text-muted-foreground">
                  {result.effectiveDays.map((d) => fmtDate(d)).join(", ")}
                </div>
              ) : (
                <p className="text-muted-foreground">
                  Kein Tag betroffen (nur Feiertage/Wochenenden im Zeitraum).
                </p>
              )}
            </div>
          )}
        </div>
        <SheetFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            disabled={!valid || mutation.isPending}
            onClick={() => mutation.mutate(false)}
          >
            Aufheben
          </Button>
          <Button
            type="button"
            disabled={!valid || mutation.isPending}
            onClick={() => mutation.mutate(true)}
          >
            Betriebsurlaub setzen
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
